import { East, NorthEast, SouthEast } from '@mui/icons-material'
import TableCell from '@mui/material/TableCell'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { calculateAssessmentTrend, TrendDirection } from '@app/src/hooks/useAssessmentTrend'
import Assessment from '@app/src/types/assessment'
import { getTranslationKeysForLevels } from '@app/src/utils/assessmentLevelTranslations'
import { Tooltip } from '@mui/material'

interface AssessmentTrendCellProps {
  assessment: Assessment
  allAssessments: Assessment[]
}

export const AssessmentTrendCell: React.FC<AssessmentTrendCellProps> = ({ assessment, allAssessments }) => {
  const { formatMessage } = useIntl()
  const trendData = calculateAssessmentTrend(assessment, allAssessments)

  const IconComponent = useMemo(() => {
    switch (trendData.trendDirection) {
      case TrendDirection.Up:
        return NorthEast
      case TrendDirection.Down:
        return SouthEast
      default:
        return East
    }
  }, [trendData.trendDirection])

  const tooltipText = useMemo(() => {
    const trendText =
      trendData.trendDirection === TrendDirection.Up
        ? formatMessage({ id: 'assessments.methodology.positive' })
        : trendData.trendDirection === TrendDirection.Down
          ? formatMessage({ id: 'assessments.methodology.negative' })
          : formatMessage({ id: 'assessments.methodology.same' })
    const previousLevel = trendData.previousAssessment?.totalLevel
    const previousYear = trendData.previousAssessment?.periodName

    const levelDescriptions = getTranslationKeysForLevels(
      assessment.assessmentTemplate?.assessmentType,
      assessment.assessmentTemplate?.levels ?? 3,
      formatMessage,
    )
    const previousLevelDescription = previousLevel
      ? levelDescriptions[previousLevel - 1]
      : formatMessage({ id: 'baselineAssessment.totalLevel' }, { value: previousLevel })

    return formatMessage({ id: 'assessments.trendTooltip' }, { trendText, previousLevelDescription, previousYear })
  }, [
    trendData.trendDirection,
    trendData.previousAssessment,
    assessment.assessmentTemplate?.assessmentType,
    assessment.assessmentTemplate?.levels,
    formatMessage,
  ])

  if (!trendData.hasTrend || !trendData.trendDirection || !trendData.previousAssessment) return <TableCell />

  return (
    <TableCell>
      <Tooltip title={tooltipText}>
        <IconComponent />
      </Tooltip>
    </TableCell>
  )
}
